<template>
  <b-container v-if="forbidden">
    <ForbiddenBanner/>
  </b-container>
  <b-container v-else>
    <b-overlay :show="showOverlay" rounded="sm">
      <b-row class="text-left">
        <b-col>
          <h2>{{ workoutName }}&#8205;</h2>
          <h5 class="text-secondary">{{ globalData.currentOrganization.name }}</h5>
        </b-col>
      </b-row>
      <b-row class="pt-4 pb-2 p-0">
        <b-col cols="3"> Workout Labels:
          <LabelSelector
              :globalData="globalData"
              :readOnly="readOnly"
              :selectedLabelUuids="labelUuids"
              v-on:selected-labels-changes="onLabelsChanged"
          />
        </b-col>
        <b-col v-if="!readOnly" cols="5" class="text-right">
          <b-button class="mr-1" size="sm" :disabled="hasErrors" variant="outline-success" @click="publish">
            Publish
            <font-awesome-icon icon="share"/>
          </b-button>
          <b-button size="sm" variant="outline-secondary" class="mode-toggle" @click="toggleMode">
            {{ mode === 'visual' ? 'Text Editor' : 'Visual Editor' }}
          </b-button>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-card class="mt-2 mb-2" border-variant="secondary">
            <h5 v-if="mode === 'yaml'">Workout Code:</h5>
            <b-card-body class="pb-0">
              <WorkoutEditor
                  v-if="mode === 'visual'"
                  :workout="workout"
                  :read-only="readOnly"
                  @change="onWorkoutChange"
              />
              <CodeMirror
                  v-else
                  id="codemirror"
                  ref="codemirror"
                  v-model="workout_yaml"
                  :options="cmOptions"
                  @input="onEdit"
              />
              <b-alert
                  show
                  v-if="hasErrors"
                  variant="warning"
                  class="text-center mt-2 mb-0 p-2"
              >
                <div v-for="(error, i) in errors" :key="i">{{ error }}</div>
              </b-alert>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-overlay>
  </b-container>
</template>

<script>
import yaml from 'js-yaml';
import WorkoutEditor from "../components/Workouts/WorkoutEditor";
import LabelSelector from "../components/Labels/LabelSelector";
import ForbiddenContentMixin from "@/components/ForbiddenContentMixin.vue";
import ForbiddenBanner from "@/components/ForbiddenBanner.vue";
import {codemirror as CodeMirror} from "vue-codemirror/src";

// A change is saved once editing pauses this long, not on every keystroke (OSA-109)
const SAVE_DELAY_MS = 400;

export default {

  name: 'Workout',
  mixins: [ForbiddenContentMixin],
  components: {
    WorkoutEditor,
    LabelSelector,
    ForbiddenBanner,
    CodeMirror
  },
  props: {
    uuid: String,
    globalData: Object
  },
  data() {
    return {
      mode: 'visual',
      showOverlay: false,
      workout_yaml: '',
      workout: {name: '', description: '', steps: []},
      labelUuids: [],
      workoutName: "",
      // Until the server says the user can edit it
      readOnly: true,
      errors: [],
      // A save waiting for editing to pause
      saveTimer: null,
      // The latest save sent: only its answer is applied
      saveSequence: 0,
      cmOptions: {
        tabSize: 2,
        mode: 'text/x-yaml',
        theme: 'default',
        lineNumbers: true,
        line: true,
      }
    }
  },
  created() {
  },
  mounted() {
    this.refresh();
  },
  beforeRouteLeave(to, from, next) {
    this.saveNow();
    next();
  },
  beforeDestroy() {
    this.saveNow();
  },
  computed: {
    codemirror() {
      return this.$refs.codemirror ? this.$refs.codemirror.codemirror : null;
    },
    hasErrors() {
      return this.errors.length > 0;
    },
  },
  methods: {
    refresh() {
      this.$http.get(`/api/workouts/${this.uuid}`)
          .then(response => {
            this.forbidden = false;
            this.readOnly = !response.data.editable;
            if (this.readOnly) {
              // Runners get the visual view only
              this.mode = 'visual';
            }
            this.workout_yaml = response.data.yaml;
            this.workout = response.data.workout;
            this.workout.meta = response.data.meta;
            this.labelUuids = response.data.meta.labelUuids ? response.data.meta.labelUuids : [];
            this.workoutName = response.data.name;
          })
          .catch(e => {
            this.HandleResponse(e);
          })
    },
    currentYaml() {
      return yaml.safeDump({
        workout: {
          name: this.workout.name,
          description: this.workout.description || '',
          steps: this.workout.steps,
        }
      });
    },
    toggleMode() {
      if (this.mode === 'visual') {
        this.workout_yaml = this.currentYaml();
        this.mode = 'yaml';
      } else {
        try {
          const parsed = yaml.safeLoad(this.workout_yaml);
          this.workout = parsed.workout;
          this.errors = [];
          this.mode = 'visual';
        } catch (e) {
          this.errors = [`Invalid YAML: ${e.message}`];
        }
      }
    },
    onWorkoutChange(updated) {
      // The page shows the change at once; the server gets it when typing pauses
      this.workout = updated;
      this.saveLater();
    },
    onLabelsChanged() {
      this.saveNow(true);
    },
    onEdit() {
      this.saveLater();
    },
    // The workout as the editor in use has it
    editedYaml() {
      return this.mode === 'yaml' ? this.workout_yaml : this.currentYaml();
    },
    saveLater() {
      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => {
        this.saveTimer = null;
        this.commit(this.editedYaml());
      }, SAVE_DELAY_MS);
    },
    /**
     * Save a change still waiting for typing to pause, e.g. before leaving the page
     *
     * @param always Save even with nothing waiting, e.g. for a change outside the editor such as the labels
     */
    saveNow(always = false) {
      if (this.saveTimer === null && !always) {
        return;
      }
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      this.commit(this.editedYaml());
    },
    publish() {
      this.showOverlay = true;
      this.$http.post(`/api/workouts/publish`,
          {workout_uuids: [this.uuid]})
          .then(() => {
            this.showOverlay = false;
          })
          .catch((e) => {
            console.warn(e)
            this.showOverlay = false;
          })
    },
    commit(workoutYaml) {
      if (this.readOnly) {
        return;
      }
      const sequence = ++this.saveSequence;
      // A newer save was sent, or a change waits to be: this answer is out of date, and applying it would undo the
      // newer typing in the editor (OSA-109)
      const outdated = () => sequence !== this.saveSequence || this.saveTimer !== null;
      this.$http.post(`/api/workouts/${this.uuid}/save`, {
            workout_yaml: workoutYaml,
            meta: {
              labelUuids: this.labelUuids,
            },
          }
      )
          .then(response => {
            if (outdated()) {
              return;
            }
            this.errors = [];
            this.workoutName = response.data.name;
            this.workout = response.data.workout;
            this.workout.meta = response.data.meta;

            if (this.mode === 'yaml' && this.codemirror) {
              const start = {line: 0, ch: 0};
              const end = {line: this.codemirror.lastLine(), ch: this.codemirror.getLine(this.codemirror.lastLine()).length};
              const marks = this.codemirror.findMarks(start, end);
              marks.forEach(marker => marker.clear());
            }
          })
          .catch(e => {
            if (outdated()) {
              return;
            }
            const data = e.response ? e.response.data : null;
            if (data && data.errors) {
              this.errors = data.errors;
            } else if (data && data.markers) {
              this.errors = data.markers.map(m => `YAML error at line ${m.line + 1}`);
              if (this.mode === 'yaml' && this.codemirror) {
                data.markers.forEach(marker => {
                  const start = {line: marker.line, ch: 0};
                  const end = {line: marker.line, ch: this.codemirror.getLine(marker.line).length};
                  this.codemirror.markText(start, end, {
                    css: "text-decoration1: wavy red underline; background-color: #f002",
                  });
                })
              }
            } else {
              this.errors = [];
            }
            this.HandleResponse(e);
          })
    },
  }
}
</script>
<style scoped>
</style>
<style>

.CodeMirror {
  border: 1px solid #ddd;
  font-size: 13px;
  height: 65vh;
}

.mode-toggle {
  display: inline-block;
  width: 120px;
}
</style>
